import { useEffect, useMemo, useState } from "react";
import { api, euro } from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { TrendUp, Coins, ChartLine, Percent, CalendarBlank, CaretDown, CaretRight, Sparkle, Users, DownloadSimple, Receipt, Scales, ArrowBendDownRight } from "@phosphor-icons/react";

const BRAND = {
  primary: "#2d5a3d",
  accent:  "#d97757",
  gold:    "#c9a961",
  slate:   "#425a70",
};
const YEAR_COLORS = { "2025": "#c9a961", "2026": "#2d5a3d", "2027": "#d97757" };

export default function Insights() {
  const [data, setData] = useState(null);
  const [expensesYear, setExpensesYear] = useState(null);
  const [openHist, setOpenHist] = useState({}); // key = year
  const [scope, setScope] = useState(String(new Date().getFullYear())); // "2025" | "2026" | "all"

  useEffect(() => {
    api.get(`/insights?period=all`).then(r => {
      setData(r.data);
      const y = new Date().getFullYear();
      setOpenHist({ [String(y)]: true });
    });
  }, []);

  // Ausgaben neu laden wenn Scope wechselt
  useEffect(() => {
    if (scope === "all") {
      // Alle Jahre summieren
      Promise.all((data?.all_years || []).map(y =>
        api.get(`/expenses/monthly`, { params: { year: y } }).then(r => r.data).catch(() => null)
      )).then(list => {
        const total = list.filter(Boolean).reduce((s, d) => s + (d.year_total || 0), 0);
        setExpensesYear({ year_total: total, months: [], _allTime: true });
      });
    } else {
      api.get(`/expenses/monthly`, { params: { year: Number(scope) } })
         .then(r => setExpensesYear(r.data))
         .catch(() => setExpensesYear({ year_total: 0, months: [] }));
    }
  }, [scope, data?.all_years]);

  const monthlyByYear = data?.monthly_by_year || {};
  const years = data?.all_years || [];

  // Line-Chart-Daten zusammenbauen (12 Monate, ein Wert pro Jahr)
  const chartData = useMemo(() => {
    if (!data) return [];
    return data.monthly_comparison || [];
  }, [data]);

  if (!data) return <div className="text-muted-foreground">Lade Wachstums-Zentrale…</div>;

  return (
    <div className="space-y-8" data-testid="insights-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl flex items-center justify-center" style={{background: BRAND.primary}}>
            <Sparkle size={22} weight="fill" color="#fff"/>
          </div>
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Wachstums-Zentrale</h1>
            <p className="text-muted-foreground text-sm">Umsatz-Übersicht · Entwicklung · Historie 2025 / 2026</p>
          </div>
        </div>
        {/* Scope-Umschalter */}
        <div className="flex flex-wrap gap-1.5" data-testid="insights-scope-selector">
          {years.slice().sort().map(y => (
            <button key={y} onClick={() => setScope(String(y))} data-testid={`insights-scope-${y}`}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
                scope === String(y) ? "bg-primary text-white shadow-md" : "bg-muted text-foreground hover:bg-primary/10"
              }`}>{y}</button>
          ))}
          <button onClick={() => setScope("all")} data-testid="insights-scope-all"
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${
              scope === "all" ? "bg-primary text-white shadow-md" : "bg-muted text-foreground hover:bg-primary/10"
            }`}>All&nbsp;Time</button>
        </div>
      </header>

      {/* Top-Kacheln */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<CalendarBlank size={20} weight="fill"/>}
                 label={`Umsatz ${data.current_month.label}`}
                 value={euro(data.current_month.gesamt)}
                 hint={`Übernachtung ${euro(data.current_month.uebernachtung)}`}
                 accent={BRAND.accent}
                 testid="kpi-current-month"/>
        <KpiCard icon={<ChartLine size={20} weight="fill"/>}
                 label={scope === "all" ? "Umsatz · All Time" : `Umsatz Gesamtjahr ${scope}`}
                 value={euro(
                   scope === "all"
                     ? (data.all_time_revenue || 0)
                     : ((data.year_compare.find(y=>y.year === Number(scope)) || {}).gesamt || 0)
                 )}
                 hint="Brutto inkl. Portale · Übern. + Verpfl. + Trinkgeld"
                 accent={BRAND.primary}
                 testid="kpi-year"/>
        <KpiCard icon={<Percent size={20} weight="fill"/>}
                 label={`Auslastung ${data.current_month.label}`}
                 value={`${data.occupancy.current_month.pct}%`}
                 hint={`${data.occupancy.current_month.booked_nights}/${data.occupancy.current_month.capacity_nights} Nächte`}
                 accent={BRAND.slate}
                 testid="kpi-occupancy"/>
        <KpiCard icon={<Coins size={20} weight="fill"/>}
                 label="All-Time Umsatz"
                 value={euro(data.all_time_revenue)}
                 hint={`seit ${Math.min(...years)} · ${data.all_time_nights} Nächte`}
                 accent={BRAND.gold}
                 testid="kpi-alltime"/>
      </section>

      {/* Portal-Provisionen + Netto-Umsatz (neue Kacheln) */}
      {(() => {
        const scopeIsAll = scope === "all";
        const yearRow    = scopeIsAll ? null : (data.year_compare || []).find(y => y.year === Number(scope));
        const gross      = scopeIsAll
          ? (data.all_time_revenue || 0)
          : (yearRow?.gesamt || 0);
        const comm       = scopeIsAll
          ? (data.all_time_portal_commissions || 0)
          : (yearRow?.portal_commissions || 0);
        const netto      = scopeIsAll
          ? (data.all_time_netto_umsatz || (gross - comm))
          : (yearRow?.netto_umsatz || (gross - comm));
        const scopeLabel = scopeIsAll ? "All Time" : String(scope);
        return (
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="commission-section">
            <KpiCard icon={<Coins size={20} weight="fill"/>}
                     label={`Brutto-Umsatz · ${scopeLabel}`}
                     value={euro(gross)}
                     hint="Vor Abzug Portal-Gebühren"
                     accent={BRAND.gold}
                     testid="kpi-gross-scope"/>
            <KpiCard icon={<ArrowBendDownRight size={20} weight="fill"/>}
                     label={`Provisionen Portale · ${scopeLabel}`}
                     value={`− ${euro(comm)}`}
                     hint="Booking.com 13,4 % + Airbnb (manuell)"
                     accent="#dc2626"
                     testid="kpi-portal-commissions"/>
            <KpiCard icon={<TrendUp size={20} weight="fill"/>}
                     label={`Netto-Umsatz · ${scopeLabel}`}
                     value={euro(netto)}
                     hint="Was tatsächlich bei dir landet"
                     accent="#059669"
                     testid="kpi-net-revenue"/>
          </section>
        );
      })()}

      {/* Gewinn (Umsatz - Ausgaben) */}
      {expensesYear && (() => {
        const yearNow = scope === "all" ? "all" : Number(scope);
        const yearRev = scope === "all"
          ? (data.all_time_revenue || 0)
          : ((data.year_compare.find(y=>y.year === yearNow) || {}).gesamt || 0);
        const yearExp = expensesYear.year_total || 0;
        const profit  = yearRev - yearExp;
        const margin  = yearRev > 0 ? Math.round((profit / yearRev) * 100) : 0;
        const scopeLabel = scope === "all" ? "All Time" : String(yearNow);
        return (
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="profit-section">
            <KpiCard icon={<Receipt size={20} weight="fill"/>}
                     label={`Ausgaben · ${scopeLabel}`}
                     value={euro(yearExp)}
                     hint="Summe aus dem Ausgaben-Journal"
                     accent="#dc2626"
                     testid="kpi-expenses-year"/>
            <KpiCard icon={<Scales size={20} weight="fill"/>}
                     label={`Gewinn · ${scopeLabel}`}
                     value={euro(profit)}
                     hint={`Umsatz ${euro(yearRev)} − Ausgaben ${euro(yearExp)}`}
                     accent={profit >= 0 ? "#059669" : "#dc2626"}
                     testid="kpi-profit-year"/>
            <KpiCard icon={<Percent size={20} weight="fill"/>}
                     label="Gewinn-Marge"
                     value={`${margin} %`}
                     hint={profit >= 0 ? "Umsatz übersteigt Ausgaben" : "Ausgaben übersteigen Umsatz"}
                     accent={profit >= 0 ? "#059669" : "#dc2626"}
                     testid="kpi-profit-margin"/>
          </section>
        );
      })()}

      {/* Wachstum */}
      {data.growth && (
        <div className="msb-card flex flex-wrap items-center gap-6 justify-between" data-testid="insights-growth">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              Wachstum {data.growth.from_year} → {data.growth.to_year}
            </div>
            <div className="text-2xl font-bold mt-1" style={{fontFamily:"'Work Sans',sans-serif"}}>
              {euro(data.growth.from)} → {euro(data.growth.to)}
            </div>
          </div>
          <div className={`text-right ${data.growth.delta >= 0 ? "text-emerald-700" : "text-red-600"}`}>
            <div className="text-3xl font-bold">
              {data.growth.delta >= 0 ? "+" : ""}{euro(data.growth.delta)}
            </div>
            {data.growth.pct != null && (
              <div className="text-sm font-semibold">
                {data.growth.pct >= 0 ? "▲" : "▼"} {Math.abs(data.growth.pct)} %
              </div>
            )}
          </div>
        </div>
      )}

      {/* Entwicklungs-Grafik */}
      <section className="msb-card" data-testid="insights-development">
        <div className="flex items-center gap-2 mb-4">
          <TrendUp size={18} weight="fill" color={BRAND.primary}/>
          <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans',sans-serif"}}>Umsatzentwicklung</h3>
          <span className="text-xs text-muted-foreground ml-auto">Monat für Monat · alle Jahre</span>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb"/>
            <XAxis dataKey="month" stroke="#6b7280" fontSize={12}/>
            <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v)=>`${(v/1000).toFixed(0)}k`}/>
            <Tooltip formatter={(v)=>euro(v)}/>
            <Legend wrapperStyle={{fontSize: 12}}/>
            {years.map((y) => (
              <Line key={y} type="monotone" dataKey={String(y)}
                    stroke={YEAR_COLORS[String(y)] || BRAND.primary}
                    strokeWidth={3}
                    dot={{ r: 4, fill: YEAR_COLORS[String(y)] || BRAND.primary }}
                    activeDot={{ r: 6 }}/>
            ))}
          </LineChart>
        </ResponsiveContainer>
      </section>

      {/* Zimmer-Ranking Umsatz (aus Excel-Monatssummen, verteilt nach Nächten) */}
      <section className="msb-card" data-testid="insights-rooms">
        <div className="flex items-center gap-2 mb-1">
          <Sparkle size={18} weight="fill" color={BRAND.accent}/>
          <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans',sans-serif"}}>Umsatz pro Zimmer</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Umsatz-Ranking über alle Zeiten · verteilt nach belegten Nächten aus Ihrer Excel-Historie
        </p>
        <div className="space-y-2">
          {(data.by_room || []).map((r, i) => (
            <div key={r.name} className="flex items-center gap-3 text-sm"
                 data-testid={`insights-room-${i}`}>
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{background: [BRAND.primary, BRAND.accent, BRAND.gold, BRAND.slate][i % 4]}}/>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{r.name}</div>
                <div className="text-xs text-muted-foreground">
                  {r.nights} Nächte · {r.pct}% des Gesamtumsatzes
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold text-base" style={{fontFamily:"'Work Sans',sans-serif"}}>{euro(r.revenue)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Umsatz-Historie je Jahr, ausklappbar */}
      <section className="space-y-3">
        {[...years].sort((a,b)=>b-a).map(y => {
          const yKey = String(y);
          const isOpen = !!openHist[yKey];
          const totals = data.year_compare.find(x=>x.year === y) || {};
          const monthList = monthlyByYear[yKey] || [];
          return (
            <div key={y} className="rounded-2xl border border-border overflow-hidden"
                 data-testid={`history-section-${y}`}>
              <button
                onClick={()=>setOpenHist(o => ({ ...o, [yKey]: !o[yKey] }))}
                className="w-full flex items-center justify-between px-5 py-4 bg-muted/40 hover:bg-muted/60 transition text-left"
                data-testid={`history-toggle-${y}`}>
                <div className="flex items-center gap-3">
                  {isOpen ? <CaretDown size={16} weight="bold"/> : <CaretRight size={16} weight="bold"/>}
                  <div>
                    <div className="font-bold text-base">Umsatz-Historie {y}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Übernachtung {euro(totals.uebernachtung || 0)} · Verpflegung {euro(totals.verpflegung || 0)} · Trinkgeld {euro(totals.trinkgeld || 0)}
                      {(totals.portal_commissions || 0) > 0 && (
                        <> · <span className="text-red-600">Provisionen Portale −{euro(totals.portal_commissions)}</span></>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-primary" style={{fontFamily:"'Work Sans',sans-serif"}}>
                    {euro(totals.gesamt || 0)}
                  </div>
                  {(totals.portal_commissions || 0) > 0 && (
                    <div className="text-[11px] text-emerald-700 font-semibold">
                      Netto {euro(totals.netto_umsatz || 0)}
                    </div>
                  )}
                </div>
              </button>
              {isOpen && (
                <div className="overflow-x-auto bg-white">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead className="bg-muted/20">
                      <tr className="text-[11px] uppercase tracking-widest text-muted-foreground">
                        <th className="text-left px-5 py-2 font-semibold">Monat</th>
                        <th className="text-right px-4 py-2 font-semibold">Übernachtung</th>
                        <th className="text-right px-4 py-2 font-semibold">Verpflegung</th>
                        <th className="text-right px-4 py-2 font-semibold">Trinkgeld</th>
                        <th className="text-right px-4 py-2 font-semibold">Auslastung</th>
                        <th className="text-right px-4 py-2 font-semibold">Brutto</th>
                        <th className="text-right px-4 py-2 font-semibold text-red-700">Provision</th>
                        <th className="text-right px-5 py-2 font-semibold text-emerald-700">Netto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthList.map(m => {
                        const empty = m.gesamt === 0;
                        const comm = m.portal_commissions || 0;
                        const netto = m.netto ?? (m.gesamt - comm);
                        return (
                          <tr key={m.month} className={`border-t border-border/50 ${empty ? "text-muted-foreground/60" : ""}`}
                              data-testid={`history-${y}-month-${m.month}`}>
                            <td className="px-5 py-2 font-medium">{m.label}</td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">{euro(m.uebernachtung)}</td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">{euro(m.verpflegung)}</td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">{euro(m.trinkgeld)}</td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">
                              <span className={`font-semibold ${m.occupancy_pct >= 60 ? "text-emerald-700" : m.occupancy_pct >= 30 ? "text-amber-700" : "text-muted-foreground"}`}>{m.occupancy_pct}%</span>
                              <span className="ml-1 text-[10px] text-muted-foreground">· {m.active_rooms}Z</span>
                            </td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">{euro(m.gesamt)}</td>
                            <td className={`px-4 py-2 text-right whitespace-nowrap ${comm > 0 ? "text-red-700 font-medium" : "text-muted-foreground/50"}`}>
                              {comm > 0 ? `− ${euro(comm)}` : "—"}
                            </td>
                            <td className={`px-5 py-2 text-right font-bold whitespace-nowrap ${empty ? "" : "text-emerald-800"}`}>
                              {euro(netto)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-primary/5 font-bold">
                      <tr className="text-sm border-t border-border">
                        <td className="px-5 py-3">Jahres-Gesamt {y}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">{euro(totals.uebernachtung || 0)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">{euro(totals.verpflegung || 0)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">{euro(totals.trinkgeld || 0)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">{(totals.occupancy_pct ?? 0)}%</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap text-primary">{euro(totals.gesamt || 0)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap text-red-700">
                          {(totals.portal_commissions || 0) > 0 ? `− ${euro(totals.portal_commissions)}` : "—"}
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap text-emerald-800 text-base">
                          {euro(totals.netto_umsatz || totals.gesamt || 0)}
                        </td>
                      </tr>
                      {(totals.portal_commissions || 0) > 0 && (
                        <tr className="text-[11px] border-t border-border text-muted-foreground" data-testid={`history-${y}-netto-info`}>
                          <td className="px-5 py-2 italic" colSpan={8}>
                            ↳ Netto = tatsächlich überwiesener Betrag nach Abzug der Portal-Provisionen (Booking.com + Airbnb)
                          </td>
                        </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </section>

      <PersonnelStats />
    </div>
  );
}

// ================= PERSONAL-STATISTIK =================
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const MONTHS_DE_SHORT = ["Jan","Feb","Mär","Apr","Mai","Jun","Jul","Aug","Sep","Okt","Nov","Dez"];
const fmtHours = (min) => {
  const h = Math.floor((min||0)/60), m = (min||0)%60;
  return `${h}:${String(m).padStart(2,"0")}`;
};

function PersonnelStats() {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);
  const [yr, setYr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    api.get("/time-entries/year-report", { params: { year }})
      .then(r => setYr(r.data))
      .finally(() => setBusy(false));
  }, [year]);

  const availableYears = useMemo(() => {
    const y = now.getFullYear();
    return [String(y-1), String(y), String(y+1)];
  }, [now]);

  const yearTotalMinutes = (yr?.users || []).reduce((s,u)=>s+(u.total_minutes||0), 0);

  const downloadPdf = () => {
    window.open(`${API_BASE}/api/time-entries/report/pdf?month=${encodeURIComponent(month)}`, "_blank");
  };

  return (
    <section className="msb-card mt-8" data-testid="personnel-stats">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Users size={22} weight="duotone" className="text-primary"/>
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Personal-Statistik</h2>
            <p className="text-xs text-muted-foreground">Arbeitsstunden pro Mitarbeiter · Monats- und Jahressumme · PDF-Export</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={year} onChange={(e)=>setYear(e.target.value)}
                  className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                  data-testid="stats-year">
            {availableYears.map(y => <option key={y} value={y}>Jahr {y}</option>)}
          </select>
          <input type="month" value={month} onChange={(e)=>setMonth(e.target.value)}
                 className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm"
                 data-testid="stats-month"/>
          <button onClick={downloadPdf}
                  className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 inline-flex items-center gap-1.5"
                  data-testid="stats-pdf-btn">
            <DownloadSimple size={14} weight="bold"/> Monat als PDF
          </button>
        </div>
      </div>

      {busy && <div className="text-sm text-muted-foreground py-4">Lade Jahresstatistik…</div>}
      {!busy && (!yr || (yr.users || []).length === 0) && (
        <div className="text-sm text-muted-foreground py-8 text-center">Keine Arbeitsstunden im Jahr {year} erfasst.</div>
      )}
      {!busy && yr && (yr.users || []).length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-[11px] uppercase tracking-widest text-muted-foreground">
                <th className="text-left p-3 min-w-[140px]">Mitarbeiter</th>
                {MONTHS_DE_SHORT.map(m => <th key={m} className="text-right p-2">{m}</th>)}
                <th className="text-right p-3 bg-primary/5 border-l border-primary/20">Jahres-Ges.</th>
              </tr>
            </thead>
            <tbody>
              {yr.users.map(u => (
                <tr key={u.user_id} className="border-t border-border" data-testid={`stats-row-${u.user_id}`}>
                  <td className="p-3 font-semibold">{u.user_name}</td>
                  {MONTHS_DE_SHORT.map((_, i) => {
                    const key = String(i+1).padStart(2,"0");
                    const cell = u.months?.[key];
                    return (
                      <td key={key} className="text-right p-2 font-mono text-xs">
                        {cell ? fmtHours(cell.minutes) : <span className="text-slate-300">·</span>}
                      </td>
                    );
                  })}
                  <td className="text-right p-3 font-mono font-bold text-primary bg-primary/5 border-l border-primary/20">
                    {fmtHours(u.total_minutes)} h
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-primary/30 bg-primary/10 font-bold">
                <td className="p-3">Gesamt {year}</td>
                {MONTHS_DE_SHORT.map((_, i) => {
                  const key = String(i+1).padStart(2,"0");
                  const sum = yr.users.reduce((s,u)=>s+(u.months?.[key]?.minutes || 0), 0);
                  return (
                    <td key={key} className="text-right p-2 font-mono text-xs">
                      {sum ? fmtHours(sum) : <span className="opacity-30">·</span>}
                    </td>
                  );
                })}
                <td className="text-right p-3 font-mono text-primary bg-primary/10 border-l border-primary/20">
                  {fmtHours(yearTotalMinutes)} h
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function KpiCard({ icon, label, value, hint, accent, testid }) {
  return (
    <div className="msb-card relative overflow-hidden" data-testid={testid}>
      <div className="absolute top-0 left-0 h-full w-1" style={{background: accent}}/>
      <div className="flex items-center gap-2 text-muted-foreground">
        <span style={{color: accent}}>{icon}</span>
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold truncate" style={{fontFamily:"'Work Sans',sans-serif"}}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1 truncate">{hint}</div>}
    </div>
  );
}
